import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaUser, FaKey, FaLock, FaEye, FaEyeSlash, FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import logo from "../../assets/grouplogo.png";
import { validatePassword } from "../../utils/validatePassword";
import zxcvbn from "zxcvbn";
import "./LoginScreen.css";
import "./ResetPasswd.css";

const ResetPasswd = () => {
  const navigate = useNavigate();

  const empIdRef = useRef(null);
  const tokenRef = useRef(null);
  const newPasswordRef = useRef(null);

  const [empId, setEmpId] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenSent, setTokenSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordStrength = zxcvbn(newPassword).score;

  useEffect(() => {
    empIdRef.current?.focus();
  }, []);

  const handleGenerateToken = async () => {
    if (!empId) {
      setError("Please enter your User ID.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post("/api/users/generateResetToken", { emp_id: empId });
      setMessage(data.message);
      setTokenSent(true);
      setTimeout(() => tokenRef.current?.focus(), 200);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send token.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!token || !newPassword || !confirmPassword) {
      setError("All fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const { isFormatStrong, zxcvbnScore, isStrong } = validatePassword(newPassword);
    if (!isStrong) {
      let msg =
        "Password must contain uppercase, lowercase, digit, special char, and be 8+ chars long.";
      if (isFormatStrong && zxcvbnScore < 3) {
        msg = "Password is too weak or common. Please choose something more unique.";
      }
      setError(msg);
      return;
    }

    setError("");
    setLoading(true);

    try {
      const { data } = await axios.post("/api/users/resetPassword", {
        emp_id: empId,
        token,
        newPassword,
      });

      if (data.success) {
        navigate("/resetSuccess", {
          state: {
            fromResetPassword: true,
          },
        });
      } else {
        setError(data.message || "Failed to reset password.");
      }
    } catch (err) {
      console.error("Reset error:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Error resetting password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true" />

      <main className="login-shell">
        <section className="login-panel reset-panel">
          <div className="login-brand">
            <img src={logo} alt="Singhal Logistics" className="login-logo" />
            <p className="login-brand-name">Singhal Logistics</p>
            <p className="login-brand-sub">Yard & Weighbridge Operations</p>
          </div>

          <h1 className="login-title">Reset Password</h1>
          <p className="login-support">
            {tokenSent
              ? "Enter the OTP from WhatsApp and set your new password."
              : "Enter your User ID to receive a reset OTP on WhatsApp."}
          </p>

          {error && (
            <div className="login-inline-error" role="alert">
              {error}
            </div>
          )}

          {message && (
            <div className="reset-success-note" role="status">
              {message}
              <small>OTP is valid for 10 minutes. Max 3 requests per day.</small>
            </div>
          )}

          <form onSubmit={handleResetPassword} className="login-form" noValidate>
            <label className="login-field">
              <span>User ID</span>
              <div className="login-input-wrap">
                <FaUser className="login-input-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Enter your user ID"
                  value={empId}
                  onChange={(e) => setEmpId(e.target.value)}
                  disabled={tokenSent || loading}
                  ref={empIdRef}
                  autoComplete="username"
                  required
                />
              </div>
            </label>

            {!tokenSent && (
              <button
                type="button"
                className="login-submit"
                onClick={handleGenerateToken}
                disabled={loading}
              >
                <FaShieldAlt aria-hidden="true" />
                {loading ? "Sending..." : "Generate Token"}
              </button>
            )}

            {tokenSent && (
              <>
                <label className="login-field">
                  <span>Token / OTP</span>
                  <div className="login-input-wrap">
                    <FaKey className="login-input-icon" aria-hidden="true" />
                    <input
                      type="text"
                      placeholder="Token received via WhatsApp"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      ref={tokenRef}
                      disabled={loading}
                      required
                    />
                  </div>
                </label>

                <label className="login-field">
                  <span>New Password</span>
                  <div className="login-input-wrap">
                    <FaLock className="login-input-icon" aria-hidden="true" />
                    <input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      ref={newPasswordRef}
                      disabled={loading}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-eye-btn"
                      onClick={() => setShowNewPassword((v) => !v)}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </label>

                <label className="login-field">
                  <span>Confirm Password</span>
                  <div className="login-input-wrap">
                    <FaLock className="login-input-icon" aria-hidden="true" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-eye-btn"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {newPassword && (
                    <div className={`reset-strength strength-${passwordStrength}`}>
                      Password strength:{" "}
                      {["Very Weak", "Weak", "Fair", "Good", "Strong"][passwordStrength]}
                    </div>
                  )}
                </label>

                <button type="submit" className="login-submit" disabled={loading}>
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </>
            )}

            <button
              type="button"
              className="login-forgot"
              onClick={() => navigate("/login")}
            >
              <FaArrowLeft style={{ marginRight: 6 }} aria-hidden="true" />
              Back to Sign in
            </button>
          </form>
        </section>
      </main>

      <footer className="login-footer">
        © {new Date().getFullYear()} Singhal Group | All Rights Reserved.
      </footer>
    </div>
  );
};

export default ResetPasswd;
